"use client" // Error boundaries must be Client Components

import { useEffect, useState } from "react"

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [isClient, setIsClient] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    // Log the error
    console.error('[Error Boundary]:', error)

    // Only log to tracking services in production
    if (process.env.NODE_ENV === 'production' && isClient) {
      // Log to Grafana Faro if available
      if (typeof window !== 'undefined' && (window as any).faro?.api) {
        (window as any).faro.api.pushError(error)
      }

      // Log to server-side error tracking
      fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: {
            message: error.message,
            name: error.name,
            stack: error.stack,
            digest: error.digest,
          },
          location: window.location.href,
        }),
      }).catch(() => {
        // Silently fail if error logging fails
      })
    }
  }, [error, isClient])

  // Hide sensitive debugging info in production
  const isDev = process.env.NODE_ENV === 'development'

  return (
    <div
      style={{
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        backgroundColor: '#f5f5f5',
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxWidth: '600px',
          width: '100%',
        }}
      >
        <h2 style={{ marginBottom: '1rem', color: '#333' }}>Something went wrong!</h2>
        
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          We encountered an unexpected error. Please try again or return to the home page.
        </p>

        {isDev && (
          <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f8f8', borderRadius: '8px' }}>
            <p style={{ marginBottom: '0.5rem', fontWeight: '600' }}>Development Details:</p>
            <p style={{ marginBottom: '0.25rem' }}><strong>Name:</strong> {error.name}</p>
            <p style={{ marginBottom: '0.25rem' }}><strong>Message:</strong> {error.message}</p>
            {error.digest && <p style={{ marginBottom: '0.25rem' }}><strong>Digest:</strong> {error.digest}</p>}
            
            <button
              onClick={() => setShowDetails(!showDetails)}
              style={{
                marginTop: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#e0e0e0',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {showDetails ? 'Hide' : 'Show'} Stack Trace
            </button>
            
            {showDetails && error.stack && (
              <pre
                style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '0.875rem',
                }}
              >
                {error.stack}
              </pre>
            )}
          </div>
        )}

        {isDev && (
          <div style={{ marginBottom: '1rem' }}>
            <h3 style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>Debug Tools:</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button
                onClick={() => {
                  const data = JSON.stringify(localStorage, null, 2)
                  navigator.clipboard.writeText(data).catch(() => {})
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#e0e0e0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Copy localStorage
              </button>
              <button
                onClick={() => {
                  const data = JSON.stringify(sessionStorage, null, 2)
                  navigator.clipboard.writeText(data).catch(() => {})
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#e0e0e0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Copy sessionStorage
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(document.cookie).catch(() => {})
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#e0e0e0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Copy cookies
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => reset()}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              backgroundColor: '#0070f3',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            Try again
          </button>
          <button
            onClick={() => (window.location.href = '/')}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              backgroundColor: '#f0f0f0',
              color: '#333',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            Go to homepage
          </button>
        </div>
      </div>
    </div>
  )
}
